/**
 * usePermission Hook
 *
 * Central hook for checking user permissions based on modulePermissions from Redux.
 * Use this hook to control UI visibility and actions based on user's role permissions.
 *
 * @example
 * // Check module-level permission
 * const { canView, canCreate, canEdit, canDelete } = usePermission('manage_companies')
 *
 * // Check submodule-level permission
 * const { canCreate } = usePermission('manage_companies', 'bill_codes')
 *
 * // Use in component
 * {canCreate && <AddButton onClick={handleAdd}>Add Company</AddButton>}
 */

import {useMemo} from 'react'
import {useAppSelector} from '../store/store-hooks'
import {resolvePermission} from './resolvePermission'
import type {PermissionCheck} from './resolvePermission'

export type {PermissionCheck} from './resolvePermission'

/**
 * Hook to check permissions for a specific module, submodule, or sub-submodule
 *
 * @param moduleKey - The module key to check (e.g., 'manage_companies', 'job_orders')
 * @param submoduleKey - Optional submodule key (e.g., 'bill_codes', 'positions')
 * @param subSubmoduleKey - Optional 3rd-level key (e.g., 'service_orders')
 * @returns PermissionCheck object with canView, canCreate, canEdit, canDelete flags
 */
export const usePermission = (
  moduleKey: string,
  submoduleKey?: string,
  subSubmoduleKey?: string,
): PermissionCheck => {
  const {backendRolePermissions} = useAppSelector((state) => state.root)

  // The resolution itself is pure (resolvePermission.ts), so code outside a hook answers the same.
  return useMemo(
    () => resolvePermission(backendRolePermissions, moduleKey, submoduleKey, subSubmoduleKey),
    [backendRolePermissions, moduleKey, submoduleKey, subSubmoduleKey],
  )
}

/**
 * Hook to check if user has ANY of the specified permissions
 * Useful for showing action columns or menus
 *
 * @example
 * const hasAnyAction = useHasAnyPermission('manage_companies', ['canEdit', 'canDelete'])
 * {hasAnyAction && <ActionColumn />}
 */
export const useHasAnyPermission = (
  moduleKey: string,
  permissions: Array<'canView' | 'canCreate' | 'canEdit' | 'canDelete'>,
  submoduleKey?: string,
  subSubmoduleKey?: string,
): boolean => {
  const permissionCheck = usePermission(moduleKey, submoduleKey, subSubmoduleKey)

  return permissions.some((perm) => permissionCheck[perm])
}

/**
 * Hook to check if user has ALL of the specified permissions
 *
 * @example
 * const canManage = useHasAllPermissions('manage_companies', ['canEdit', 'canDelete'])
 */
export const useHasAllPermissions = (
  moduleKey: string,
  permissions: Array<'canView' | 'canCreate' | 'canEdit' | 'canDelete'>,
  submoduleKey?: string,
  subSubmoduleKey?: string,
): boolean => {
  const permissionCheck = usePermission(moduleKey, submoduleKey, subSubmoduleKey)

  return permissions.every((perm) => permissionCheck[perm])
}

/**
 * Hook that returns true when the current user is a platform admin.
 *
 * Platform admins are granted full access by convention across the app
 * (see the isPlatform bypass in useBackendTabPermissions, and the route guard
 * in AppRoutes, which lets Platform through only when a route's node is
 * absent). Use this to OR a platform bypass into feature-specific
 * usePermission checks so newly-added granular permissions that aren't yet
 * seeded onto the platformadmin role don't hide features from platform admins.
 */
export const useIsPlatformAdmin = (): boolean => {
  const {backendRolePermissions} = useAppSelector((state) => state.root)
  return !!backendRolePermissions?.isPlatform
}

export default usePermission
